import React, { useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '../components/Navbar';
import { TYPOGRAPHY, PLACEHOLDER_IMAGES } from '../constants/typography';

gsap.registerPlugin(ScrollTrigger);

const ServicesPage = () => {
    const sectionRefs = useRef({});

    // Icon rendering helper
    const renderIcon = (iconName, size = 24) => {
        const icons = {
            lightbulb: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>,
            ruler: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.42 7.92l-5.34-5.34a1 1 0 0 0-1.42 0l-12 12a1 1 0 0 0 0 1.42l5.34 5.34a1 1 0 0 0 1.42 0l12-12a1 1 0 0 0 0-1.42z"/><path d="M6.5 6.5l1 1"/><path d="M10.5 10.5l1 1"/><path d="M14.5 14.5l1 1"/><path d="M18.5 18.5l1 1"/></svg>,
            sparkles: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3l1.545 4.635L18 9.18l-4.635 1.545L12 15.36l-1.545-4.635L6 9.18l4.635-1.545L12 3z"/><path d="M19 14l.897 2.69L22 17.59l-2.103.9L19 21l-.897-2.69L16 17.59l2.103-.9L19 14z"/><path d="M5 5l.897 2.69L8 8.59l-2.103.9L5 12l-.897-2.69L2 8.59l2.103-.9L5 5z"/></svg>,
            globe: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
            zap: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
            chair: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"/><path d="M2 11h20"/><path d="M2 16h20"/><path d="M5 11v10"/><path d="M19 11v10"/></svg>,
            hammer: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 12-8.5 8.5c-.83.83-2.17.83-3 0 0 0 0 0 0 0a2.12 2.12 0 0 1 0-3L12 9"/><path d="M17.64 15L22 10.64"/><path d="m20.91 11.7-1.25-1.25c-.6-.6-.93-1.4-.93-2.25v-.86L16.01 4.6a5.56 5.56 0 0 0-3.94-1.64H9l.92.82A6.18 6.18 0 0 1 12 8.4v1.56l2 2h2.47l2.26 1.91"/></svg>,
            trophy: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/></svg>,
            smile: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>,
            palette: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>,
            diamond: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41l-7.59-7.59a2.41 2.41 0 0 0-3.41 0L2.7 10.3z"/></svg>,
            handshake: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 12h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 14"/><path d="m7 18 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"/><path d="m2 13 6 6"/></svg>,
            dollar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
            message: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
            clipboard: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>,
            cart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>,
            gift: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>
        };
        return icons[iconName] || icons.lightbulb;
    };

    // Services data
    const services = useMemo(() => [
        {
            id: 'consulting',
            name: 'Consulting & Planning',
            icon: 'lightbulb',
            tagline: 'Transform Ideas Into Reality',
            description: 'We provide personalized consultation and comprehensive planning services to bring your vision to life. Our expert team guides you through every step.',
            features: ['Personal Consultation', '3D Visualization', 'Budget Planning', 'Design Strategy'],
            gradient: 'linear-gradient(135deg, rgba(201, 169, 110, 0.8) 0%, rgba(139, 115, 85, 0.8) 100%)',
            image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800'
        },
        {
            id: 'space-planning',
            name: 'Space Planning',
            icon: 'ruler',
            tagline: 'Optimize Every Square Foot',
            description: 'Strategic space utilization that maximizes functionality while maintaining aesthetic excellence. We create layouts that work for your lifestyle.',
            features: ['3D Layout Design', 'Traffic Flow Analysis', 'Zoning & Functionality', 'Ergonomic Planning'],
            gradient: 'linear-gradient(135deg, rgba(184, 147, 93, 0.8) 0%, rgba(201, 169, 110, 0.8) 100%)',
            image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=800'
        },
        {
            id: 'interior-concepts',
            name: 'Interior Concepts',
            icon: 'sparkles',
            tagline: 'Design That Inspires',
            description: 'Complete interior design solutions that reflect your personality and style. We create spaces that tell your story through thoughtful design.',
            features: ['Complete Design', 'Style Curation', 'Color Consultation', 'Material Selection'],
            gradient: 'linear-gradient(135deg, rgba(139, 115, 85, 0.8) 0%, rgba(106, 90, 67, 0.8) 100%)',
            image: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=800'
        },
        {
            id: 'material-sourcing',
            name: 'Material Sourcing',
            icon: 'globe',
            tagline: 'Premium Materials Worldwide',
            description: 'Global sourcing network providing access to the finest materials and finishes. We ensure quality, authenticity, and competitive pricing.',
            features: ['Global Network', 'Quality Assurance', 'Competitive Pricing', 'Authenticity Guarantee'],
            gradient: 'linear-gradient(135deg, rgba(201, 169, 110, 0.8) 0%, rgba(184, 147, 93, 0.8) 100%)',
            image: 'https://images.unsplash.com/photo-1615874959474-d609969a20ed?w=800'
        },
        {
            id: 'project-management',
            name: 'Project Management',
            icon: 'zap',
            tagline: 'Seamless Execution',
            description: 'End-to-end project management ensuring timely delivery and quality workmanship. We coordinate all aspects so you don\'t have to worry.',
            features: ['Timeline Management', 'Vendor Coordination', 'Quality Control', 'Budget Oversight'],
            gradient: 'linear-gradient(135deg, rgba(139, 115, 85, 0.8) 0%, rgba(201, 169, 110, 0.8) 100%)',
            image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=800'
        },
        {
            id: 'custom-furniture',
            name: 'Custom Furniture',
            icon: 'chair',
            tagline: 'Bespoke Creations',
            description: 'Custom-designed furniture crafted to your exact specifications. From concept to creation, we bring your unique vision to life.',
            features: ['Custom Design', 'OEM/ODM Services', 'Quality Craftsmanship', 'Unique Pieces'],
            gradient: 'linear-gradient(135deg, rgba(106, 90, 67, 0.8) 0%, rgba(139, 115, 85, 0.8) 100%)',
            image: 'https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?w=800'
        },
        {
            id: 'renovation',
            name: 'Renovation Services',
            icon: 'hammer',
            tagline: 'Renew & Refresh',
            description: 'Complete renovation services that breathe new life into existing spaces. We handle everything from planning to execution.',
            features: ['Space Transformation', 'Minimal Disruption', 'Quality Work', 'On-Time Delivery'],
            gradient: 'linear-gradient(135deg, rgba(184, 147, 93, 0.8) 0%, rgba(139, 115, 85, 0.8) 100%)',
            image: 'https://images.unsplash.com/photo-1615529182904-14819c35db37?w=800'
        }
    ], []);

    useEffect(() => {
        // Animate sections on scroll
        gsap.utils.toArray('.service-card').forEach((card, index) => {
            gsap.fromTo(card,
                { opacity: 0, y: 100 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 85%',
                        end: 'top 60%',
                        toggleActions: 'play none none reverse'
                    }
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, []);

    return (
        <div style={{ background: '#0A0A0A' }}>
            <Helmet>
                <title>Our Services - Hav'n Interiors</title>
                <meta name="description" content="Comprehensive interior design services from consulting to project management." />
            </Helmet>

            <Navbar />

            {/* Hero Section - More Dynamic */}
            <section style={{
                position: 'relative',
                padding: '180px 60px 100px',
                background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${PLACEHOLDER_IMAGES.design})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                overflow: 'hidden'
            }}>
                {/* Animated Background Elements */}
                <div style={{
                    position: 'absolute',
                    top: '20%',
                    right: '10%',
                    width: '400px',
                    height: '400px',
                    background: 'radial-gradient(circle, rgba(201, 169, 110, 0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    filter: 'blur(60px)',
                    animation: 'float 6s ease-in-out infinite'
                }}></div>
                <div style={{
                    position: 'absolute',
                    bottom: '10%',
                    left: '5%',
                    width: '300px',
                    height: '300px',
                    background: 'radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    filter: 'blur(60px)',
                    animation: 'float 8s ease-in-out infinite reverse'
                }}></div>
                
                <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
                    <div style={{
                        display: 'inline-block',
                        padding: '8px 20px',
                        background: 'rgba(201, 169, 110, 0.1)',
                        border: '1px solid rgba(201, 169, 110, 0.3)',
                        borderRadius: '30px',
                        marginBottom: '30px',
                        fontSize: '14px',
                        letterSpacing: '2px',
                        textTransform: 'uppercase',
                        color: 'var(--accent-color)'
                    }}>
                        Our Services
                    </div>
                    
                    <h1 style={{
                        fontSize: 'clamp(48px, 8vw, 92px)',
                        fontWeight: 200,
                        letterSpacing: '-3px',
                        marginBottom: '25px',
                        color: 'var(--text-light)',
                        lineHeight: 1.1
                    }}>
                        Complete Design<br/>
                        <span style={{ 
                            background: 'linear-gradient(135deg, #C9A96E 0%, #F5E6D3 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}>Solutions</span>
                    </h1>
                    
                    <p style={{
                        fontSize: '20px',
                        color: 'rgba(245, 241, 237, 0.7)',
                        maxWidth: '700px',
                        margin: '0 auto 40px',
                        lineHeight: 1.8
                    }}>
                        From vision to reality, we handle every aspect of your interior design journey with expertise and precision.
                    </p>

                    <Link to="/contact" style={{
                        display: 'inline-block',
                        padding: '18px 40px',
                        background: 'var(--accent-color)',
                        color: '#000',
                        textDecoration: 'none',
                        borderRadius: '50px',
                        fontSize: '16px',
                        fontWeight: 500,
                        letterSpacing: '1px',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 10px 30px rgba(201, 169, 110, 0.3)'
                    }}
                    className="hero-cta-btn"
                    >
                        Start Your Project →
                    </Link>
                </div>
            </section>

            {/* Stats Section */}
            <section style={{
                padding: '80px 60px',
                background: '#0F0F0F',
                borderTop: '1px solid rgba(201, 169, 110, 0.2)',
                borderBottom: '1px solid rgba(201, 169, 110, 0.2)',
                display: 'flex',
                alignItems: 'center',
                minHeight: '300px'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '40px',
                        textAlign: 'center'
                    }}>
                        {[
                            { number: '500+', label: 'Projects Completed', icon: 'sparkles' },
                            { number: '15+', label: 'Years Experience', icon: 'trophy' },
                            { number: '98%', label: 'Client Satisfaction', icon: 'smile' },
                            { number: '8', label: 'Specialized Studios', icon: 'palette' }
                        ].map((stat, idx) => (
                            <div key={idx} style={{ position: 'relative' }}>
                                <div style={{
                                    marginBottom: '10px',
                                    color: 'var(--accent-color)',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}>
                                    {renderIcon(stat.icon, 48)}
                                </div>
                                <div style={{
                                    fontSize: '48px',
                                    fontWeight: 200,
                                    color: 'var(--accent-color)',
                                    marginBottom: '10px',
                                    letterSpacing: '-2px'
                                }}>
                                    {stat.number}
                                </div>
                                <div style={{
                                    fontSize: '14px',
                                    letterSpacing: '1px',
                                    color: 'rgba(245, 241, 237, 0.7)',
                                    textTransform: 'uppercase'
                                }}>
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section style={{
                padding: '100px 60px',
                background: 'linear-gradient(180deg, #0A0A0A 0%, #0F0F0F 100%)',
                position: 'relative'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center', marginBottom: '60px' }}>
                    <h2 style={{
                        fontSize: 'clamp(36px, 5vw, 56px)',
                        fontWeight: 300,
                        color: 'var(--text-light)',
                        marginBottom: '20px',
                        letterSpacing: '-1px'
                    }}>
                        Why Choose Hav'n Interiors
                    </h2>
                    <p style={{
                        fontSize: '18px',
                        color: 'rgba(245, 241, 237, 0.7)',
                        maxWidth: '700px',
                        margin: '0 auto'
                    }}>
                        We combine creativity, expertise, and dedication to deliver exceptional results
                    </p>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '30px',
                    maxWidth: '1200px',
                    margin: '0 auto'
                }}>
                    {[
                        {
                            icon: 'palette',
                            title: 'Creative Excellence',
                            desc: 'Award-winning designs that blend aesthetics with functionality',
                            image: PLACEHOLDER_IMAGES.design
                        },
                        {
                            icon: 'zap',
                            title: 'On-Time Delivery',
                            desc: 'Strict timeline adherence with quality project management',
                            image: PLACEHOLDER_IMAGES.planning
                        },
                        {
                            icon: 'diamond',
                            title: 'Premium Quality',
                            desc: 'Only the finest materials and skilled craftsmanship',
                            image: PLACEHOLDER_IMAGES.materials
                        },
                        {
                            icon: 'handshake',
                            title: 'Dedicated Support',
                            desc: '24/7 customer service and post-project assistance',
                            image: PLACEHOLDER_IMAGES.team
                        },
                        {
                            icon: 'dollar',
                            title: 'Value for Money',
                            desc: 'Competitive pricing without compromising on quality',
                            image: PLACEHOLDER_IMAGES.consultation
                        },
                        {
                            icon: 'globe',
                            title: 'Global Network',
                            desc: 'Access to international brands and materials',
                            image: PLACEHOLDER_IMAGES.office
                        }
                    ].map((feature, idx) => (
                        <div key={idx} style={{
                            background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
                            borderRadius: '16px',
                            border: '1px solid rgba(201, 169, 110, 0.2)',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                        className="why-choose-card"
                        >
                            {/* Front Image */}
                            <div style={{
                                height: '200px',
                                overflow: 'hidden',
                                position: 'relative'
                            }}>
                                <img
                                    src={feature.image}
                                    alt={feature.title}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        transition: 'transform 0.3s ease'
                                    }}
                                    className="feature-card-image"
                                />
                                <div style={{
                                    position: 'absolute',
                                    top: '15px',
                                    right: '15px',
                                    background: 'rgba(0, 0, 0, 0.6)',
                                    borderRadius: '50%',
                                    width: '50px',
                                    height: '50px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backdropFilter: 'blur(10px)',
                                    color: 'var(--accent-color)'
                                }}>
                                    {renderIcon(feature.icon, 28)}
                                </div>
                            </div>

                            {/* Content */}
                            <div style={{ padding: '25px' }}>
                                <h3 style={{
                                    fontSize: '22px',
                                    fontWeight: 500,
                                    color: 'var(--text-light)',
                                    marginBottom: '12px'
                                }}>
                                    {feature.title}
                                </h3>
                                <p style={{
                                    fontSize: '15px',
                                    color: 'rgba(245, 241, 237, 0.7)',
                                    lineHeight: 1.7
                                }}>
                                    {feature.desc}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* How We Work Section */}
            <section style={{
                padding: '100px 60px',
                background: '#0F0F0F',
                position: 'relative'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '70px' }}>
                        <h2 style={{
                            fontSize: 'clamp(36px, 5vw, 56px)',
                            fontWeight: 300,
                            color: 'var(--text-light)',
                            marginBottom: '20px',
                            letterSpacing: '-1px'
                        }}>
                            Our Process
                        </h2>
                        <p style={{
                            fontSize: '18px',
                            color: 'rgba(245, 241, 237, 0.7)',
                            maxWidth: '700px',
                            margin: '0 auto'
                        }}>
                            Simple, transparent, and effective workflow from concept to completion
                        </p>
                    </div>

                    <div className="responsive-grid grid-5" style={{
                        display: 'flex',
                        gap: '30px',
                        position: 'relative',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        maxWidth: '1400px',
                        margin: '0 auto'
                    }}>
                        {[
                            { step: '01', title: 'Consultation', desc: 'Understanding your vision and requirements', icon: 'message' },
                            { step: '02', title: 'Planning', desc: '3D designs and detailed project planning', icon: 'clipboard' },
                            { step: '03', title: 'Sourcing', desc: 'Material selection and procurement', icon: 'cart' },
                            { step: '04', title: 'Execution', desc: 'Professional installation and project management', icon: 'hammer' },
                            { step: '05', title: 'Delivery', desc: 'Final touches and handover', icon: 'gift' }
                        ].map((process, idx) => (
                            <div key={idx} style={{
                                textAlign: 'center',
                                position: 'relative',
                                flex: '1'
                            }}>
                                <div style={{
                                    width: '90px',
                                    height: '90px',
                                    margin: '0 auto 20px',
                                    background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.2) 0%, rgba(201, 169, 110, 0.05) 100%)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid var(--accent-color)',
                                    position: 'relative',
                                    boxShadow: '0 0 30px rgba(201, 169, 110, 0.2)',
                                    color: 'var(--accent-color)'
                                }}>
                                    {renderIcon(process.icon, 40)}
                                    <div style={{
                                        position: 'absolute',
                                        top: '-5px',
                                        right: '-5px',
                                        width: '35px',
                                        height: '35px',
                                        background: 'var(--accent-color)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '15px',
                                        fontWeight: '700',
                                        color: '#000',
                                        border: '2px solid #0F0F0F',
                                        zIndex: 2
                                    }}>
                                        {process.step}
                                    </div>
                                </div>
                                <h3 style={{
                                    fontSize: '20px',
                                    fontWeight: 500,
                                    color: 'var(--text-light)',
                                    marginBottom: '10px'
                                }}>
                                    {process.title}
                                </h3>
                                <p style={{
                                    fontSize: '14px',
                                    color: 'rgba(245, 241, 237, 0.7)',
                                    lineHeight: 1.6
                                }}>
                                    {process.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid - Modern Card Layout */}
            <section style={{
                padding: '100px 60px',
                background: '#0A0A0A'
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '70px' }}>
                        <h2 style={{
                            fontSize: 'clamp(36px, 5vw, 56px)',
                            fontWeight: 300,
                            color: 'var(--text-light)',
                            marginBottom: '20px',
                            letterSpacing: '-1px'
                        }}>
                            Our Services
                        </h2>
                        <p style={{
                            fontSize: '18px',
                            color: 'rgba(245, 241, 237, 0.7)',
                            maxWidth: '700px',
                            margin: '0 auto'
                        }}>
                            Comprehensive solutions tailored to your unique needs
                        </p>
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
                        gap: '40px'
                    }}>
                        {services.map((service, index) => (
                            <div
                                key={service.id}
                                id={service.id}
                                ref={el => sectionRefs.current[service.id] = el}
                                className="service-card"
                                style={{
                                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                                    borderRadius: '24px',
                                    overflow: 'hidden',
                                    border: '1px solid rgba(201, 169, 110, 0.2)',
                                    transition: 'all 0.4s ease',
                                    position: 'relative'
                                }}
                            >
                                {/* Gradient Header */}
                                <div style={{
                                    background: service.gradient,
                                    padding: '40px 35px',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        marginBottom: '15px',
                                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                                        color: '#fff',
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}>
                                        {renderIcon(service.icon, 48)}
                                    </div>
                                    <h2 style={{
                                        fontSize: '28px',
                                        fontWeight: 500,
                                        color: '#fff',
                                        marginBottom: '8px',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}>
                                        {service.name}
                                    </h2>
                                    <p style={{
                                        fontSize: '16px',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        fontWeight: 300,
                                        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                    }}>
                                        {service.tagline}
                                    </p>
                                </div>

                                {/* Image */}
                                <div style={{
                                    height: '220px',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}>
                                    <img
                                        src={service.image}
                                        alt={service.name}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transition: 'transform 0.4s ease'
                                        }}
                                        className="service-card-image"
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: '50%',
                                        background: 'linear-gradient(0deg, rgba(26, 26, 46, 0.9) 0%, transparent 100%)'
                                    }}></div>
                                </div>

                                {/* Content */}
                                <div style={{ padding: '35px' }}>
                                    <p style={{
                                        fontSize: '15px',
                                        lineHeight: 1.8,
                                        color: 'rgba(245, 241, 237, 0.85)',
                                        marginBottom: '25px'
                                    }}>
                                        {service.description}
                                    </p>

                                    {/* Features */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '12px',
                                        marginBottom: '25px'
                                    }}>
                                        {service.features.map((feature, idx) => (
                                            <div key={idx} style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '14px',
                                                color: 'rgba(245, 241, 237, 0.9)'
                                            }}>
                                                <span style={{
                                                    color: 'var(--accent-color)',
                                                    fontSize: '16px'
                                                }}>✓</span>
                                                {feature}
                                            </div>
                                        ))}
                                    </div>

                                    {/* CTA Button */}
                                    <Link to="/contact" style={{
                                        display: 'block',
                                        padding: '14px 24px',
                                        background: 'rgba(201, 169, 110, 0.1)',
                                        color: 'var(--accent-color)',
                                        textDecoration: 'none',
                                        borderRadius: '12px',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        textAlign: 'center',
                                        transition: 'all 0.3s ease',
                                        border: '1px solid rgba(201, 169, 110, 0.3)'
                                    }}
                                    className="service-card-btn"
                                    >
                                        Learn More →
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section style={{
                padding: '120px 60px',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #0F0F0F 100%)',
                borderTop: '2px solid var(--accent-color)',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '600px',
                    height: '600px',
                    background: 'radial-gradient(circle, rgba(201, 169, 110, 0.05) 0%, transparent 70%)',
                    borderRadius: '50%',
                    filter: 'blur(80px)'
                }}></div>

                <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <h2 style={{
                        fontSize: 'clamp(36px, 5vw, 64px)',
                        fontWeight: 300,
                        color: 'var(--text-light)',
                        marginBottom: '25px',
                        letterSpacing: '-1px'
                    }}>
                        Ready to Create Your<br/>
                        <span style={{ color: 'var(--accent-color)' }}>Dream Space?</span>
                    </h2>
                    <p style={{
                        fontSize: '18px',
                        color: 'rgba(245, 241, 237, 0.7)',
                        marginBottom: '45px',
                        lineHeight: 1.8
                    }}>
                        Let's discuss how we can bring your interior design vision to life.
                        Contact us today for a free consultation.
                    </p>
                    <div style={{
                        display: 'flex',
                        gap: '20px',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <Link to="/contact" style={{
                            display: 'inline-block',
                            padding: '20px 50px',
                            background: 'var(--accent-color)',
                            color: '#000',
                            textDecoration: 'none',
                            borderRadius: '50px',
                            fontSize: '16px',
                            fontWeight: 600,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            transition: 'all 0.3s ease',
                            border: '2px solid var(--accent-color)',
                            boxShadow: '0 10px 30px rgba(201, 169, 110, 0.3)'
                        }}
                        className="cta-btn-primary"
                        >
                            Get Free Consultation
                        </Link>
                        <Link to="/studios" style={{
                            display: 'inline-block',
                            padding: '20px 50px',
                            background: 'transparent',
                            color: 'var(--accent-color)',
                            textDecoration: 'none',
                            borderRadius: '50px',
                            fontSize: '16px',
                            fontWeight: 600,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            transition: 'all 0.3s ease',
                            border: '2px solid var(--accent-color)'
                        }}
                        className="cta-btn-secondary"
                        >
                            Explore Studios
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer style={{
                background: '#0A0A0A',
                padding: '40px 60px',
                textAlign: 'center',
                borderTop: '1px solid rgba(201, 169, 110, 0.2)'
            }}>
                <img src={`${process.env.PUBLIC_URL}/current-logo.png`} alt="Hav'n Interiors" style={{
                    height: '50px',
                    marginBottom: '20px',
                    opacity: 0.9
                }} />
                <p style={{
                    color: 'rgba(245, 241, 237, 0.6)',
                    fontSize: '14px',
                    margin: 0
                }}>
                    &copy; 2025 Hav'n Interiors & Global Sourcing. All Rights Reserved.
                </p>
                <p style={{
                    marginTop: '10px',
                    fontSize: '12px',
                    color: 'rgba(245, 241, 237, 0.4)'
                }}>
                    Innovate. Integrate. Execute.
                </p>
            </footer>

            {/* Animations CSS */}
            <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }

                .hero-cta-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 15px 40px rgba(201, 169, 110, 0.4) !important;
                }

                .why-choose-card:hover {
                    transform: translateY(-5px);
                    border-color: var(--accent-color) !important;
                    box-shadow: 0 15px 40px rgba(201, 169, 110, 0.2);
                }

                .why-choose-card:hover .feature-card-image {
                    transform: scale(1.1);
                }

                .service-card {
                    position: relative;
                }

                .service-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
                    border-color: var(--accent-color) !important;
                }

                .service-card:hover .service-card-image {
                    transform: scale(1.1);
                }

                .service-card-btn:hover {
                    background: var(--accent-color) !important;
                    color: #000 !important;
                    border-color: var(--accent-color) !important;
                }

                .cta-btn-primary:hover {
                    background: transparent !important;
                    color: var(--accent-color) !important;
                    transform: translateY(-3px);
                }

                .cta-btn-secondary:hover {
                    background: var(--accent-color) !important;
                    color: #000 !important;
                    transform: translateY(-3px);
                }
            `}</style>
        </div>
    );
};



export default ServicesPage;
